// Glassmorphism redesign tokens mapped from unified design system
import { useMemo } from 'react';
import { PRIMITIVE_COLORS, COMPONENT_COLORS, REDESIGN_COLORS } from '../residentColors';
import { useThemeColors } from '@/hooks/residents/useThemeColors';

export const NOTIFICATION_COLORS = {
  screenBackground: REDESIGN_COLORS.bg.base,
  cardBackground: REDESIGN_COLORS.card.bg,
  cardBackgroundRead: 'transparent',
  cardBorder: REDESIGN_COLORS.card.border,
  cardBorderRead: REDESIGN_COLORS.card.border,
  avatarBackground: REDESIGN_COLORS.glass.buttonBg,
  avatarBackgroundDesktop: REDESIGN_COLORS.activityIcon.bg,
  circleIconBackground: REDESIGN_COLORS.activityIcon.bg,
  glassButtonBg: REDESIGN_COLORS.glass.buttonBg,
  glassButtonBorder: REDESIGN_COLORS.glass.buttonBorder,
  borderPrimary: PRIMITIVE_COLORS.resioBlueLight,
  borderSecondary: REDESIGN_COLORS.separator,
  modalBorder: REDESIGN_COLORS.card.border,
  textPrimary: REDESIGN_COLORS.text.primary,
  textSecondary: REDESIGN_COLORS.text.muted,
  textTertiary: REDESIGN_COLORS.text.subtle,
  unreadDot: REDESIGN_COLORS.accent.red,
  linkBlue: PRIMITIVE_COLORS.resioBlueLight,
  buttonPrimary: PRIMITIVE_COLORS.resioBlue,
  iconDefault: REDESIGN_COLORS.text.muted,
  iconMuted: REDESIGN_COLORS.text.subtle,
  iconInactive: REDESIGN_COLORS.text.dim,
  refreshTint: REDESIGN_COLORS.text.primary,
  switchTrackOff: COMPONENT_COLORS.toggle.trackOff,
  switchTrackOn: COMPONENT_COLORS.toggle.trackOn,
  switchThumb: COMPONENT_COLORS.toggle.thumb,
  switchTrackOnDesktop: '#009688',
} as const;

export const NOTIFICATION_AVATAR = { size: 46, borderRadius: 23, iconSize: 20 } as const;
export const NOTIFICATION_AVATAR_DESKTOP = { size: 36, borderRadius: 18 } as const;
export const NOTIFICATION_UNREAD_DOT = { size: 10, borderRadius: 5 } as const;
export const NOTIFICATION_UNREAD_DOT_DESKTOP = { size: 12, borderRadius: 6, offsetTop: -2, offsetLeft: -2 } as const;

export type NotificationColor = keyof typeof NOTIFICATION_COLORS;

export function useNotificationColors() {
  const { semantic, components, colors } = useThemeColors();
  return useMemo(() => ({
    screenBackground: colors.bg.base,
    cardBackground: colors.card.bg,
    cardBackgroundRead: 'transparent' as const,
    cardBorder: colors.card.border,
    cardBorderRead: colors.card.border,
    avatarBackground: colors.glass.buttonBg,
    avatarBackgroundDesktop: colors.activityIcon.bg,
    circleIconBackground: colors.activityIcon.bg,
    glassButtonBg: colors.glass.buttonBg,
    glassButtonBorder: colors.glass.buttonBorder,
    borderPrimary: semantic.interactive.primaryHover,
    borderSecondary: colors.separator,
    modalBorder: colors.card.border,
    textPrimary: colors.text.primary,
    textSecondary: colors.text.muted,
    textTertiary: colors.text.subtle,
    unreadDot: colors.accent.red,
    linkBlue: semantic.text.link,
    buttonPrimary: semantic.interactive.primary,
    iconDefault: colors.text.muted,
    iconMuted: colors.text.subtle,
    iconInactive: colors.text.dim,
    refreshTint: colors.text.primary,
    switchTrackOff: components.toggle.trackOff,
    switchTrackOn: components.toggle.trackOn,
    switchThumb: components.toggle.thumb,
    switchTrackOnDesktop: components.toggle.trackOn,
  }), [semantic, components, colors]);
}
